import {DataTable} from "../DataTable/DataTable";
import {GeofenceSiteGroupEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Box, Modal, ModalProps} from "@mantine/core";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useGeofenceSiteGroupDataTableFilter} from "../GeofenceSiteGroupDataTable/useGeofenceSiteGroupDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {useNavigate} from "react-router-dom";
import {useGeofenceSiteGroupAction} from "../../menu/GeofenceSiteGroupActionMenu/useGeofenceSiteGroupAction";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";

const EMPTY_ARRAY: GeofenceSiteGroupEntity[] = []

export type GeofenceSiteGroupDataTableProps = EntityDataTableProps<GeofenceSiteGroupEntity>

export const GeofenceSiteGroupDataTable = (props: GeofenceSiteGroupDataTableProps) => {
  const {
    records: roles = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {filteredRecords, filterConfig} = useGeofenceSiteGroupDataTableFilter({
    records: roles
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    onGeofenceSiteGroupActionClick
  } = useGeofenceSiteGroupAction()

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: GeofenceSiteGroupEntity) => {
    e.stopPropagation()
    navigate(`/geofence-site-groups/profile?id=${record.id}`)
  }, [navigate])

  const onEditActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: GeofenceSiteGroupEntity) => {
    e.stopPropagation()
    onGeofenceSiteGroupActionClick(record, 'EDIT')
  }, [onGeofenceSiteGroupActionClick])

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: GeofenceSiteGroupEntity) => {
    e.stopPropagation()
    onGeofenceSiteGroupActionClick(record, 'DELETE')
  }, [onGeofenceSiteGroupActionClick])

  const columns = useMemo(() => {
    const values: DataTableColumn<GeofenceSiteGroupEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.geofencesitegroup.table.column.name", "Name"),
        accessor: 'name',
        width: 400,
        sortable: true,
        render: (record) => (
          <EntityAnchor value={record}/>
        ),
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: (geofenceSiteGroup) => (
          <EntityActionGroup
            permissionPrefix={"geofencesitegroup"}
            onViewActionClick={(e) => onViewActionClick(e, geofenceSiteGroup)}
            onEditActionClick={(e) => onEditActionClick(e, geofenceSiteGroup)}
            onDeleteActionClick={(e) => onDeleteActionClick(e, geofenceSiteGroup)}
          />
        )
      }
    ]

    return values
  }, [filterConfig, onEditActionClick, onViewActionClick, onDeleteActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type GeofenceSiteGroupDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<GeofenceSiteGroupDataTableProps, 'recordsPerPageOptions'>
}

export const GeofenceSiteGroupDataTableModal = (props: GeofenceSiteGroupDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.geofencesitegroup.plural", "Geofence Site Groups"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <GeofenceSiteGroupDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
